<script lang="ts">
import {useMetersStore} from '@/stores/meters.ts';
import PerformanceChart from '@/components/PerformanceChart.vue';
import SatelliteApi from '@/data/SatelliteApi';
import PerformanceChartHourly from '@/components/PerformanceChartHourly.vue';
import {
  addDays,
  format,
  getDaysInMonth,
  getHours,
  getMonth,
  getWeek,
  getYear,
  startOfDay,
  startOfMonth,
  subDays
} from "date-fns";
import {nl} from "date-fns/locale";

export default {
  name: 'ChartStatistics',
  components: {PerformanceChartHourly, PerformanceChart},
  props: {},
  data() {
    return {
      meterStore: useMetersStore(),
      selectedPeriod: getMonth(new Date()),
      performances: [],
      performancesForWeek: null,
      performancesForMonth: null,
      loading: true,
    };
  },
  mounted() {
    this.handlePeriodSelected();
  },
  computed: {
    currentWeekNumber() {
      return getWeek(new Date);
    },
    currentMonthNumber() {
      return getMonth(new Date());
    },
    currentMonthString() {
      return format(new Date(), 'MMMM', { locale: nl })
    },
    availablePeriods() {
      let options = [
        {label: 'Deze maand', value: this.currentMonthNumber},
        {label: 'Deze week', value: this.currentWeekNumber},
      ];

      const allPerformances = this.meterStore.getMeterDetails.performances;
      const years = allPerformances
        .map((p) =>
          ({
            label: p.start_date.substring(0, 4),
            value: getYear(p.start_date),
          }))
        .reverse();

      // Get unique years
      let yearsAsJsonSet = new Set(years.map(JSON.stringify));
      const yearsArray = Array.from(yearsAsJsonSet).map(JSON.parse);

      options = options.concat(yearsArray);

      return options;
    },
  },
  methods: {
    handlePeriodSelected() {
      let performancePromise;

      switch (this.selectedPeriod) {
        case this.currentWeekNumber:
          performancePromise = this.performancesForThisWeek();
          break;
        case this.currentMonthNumber:
          performancePromise = this.performancesForThisMonth();
          break;
        default:
          performancePromise = this.performancesForYear();
          break;
      }

      performancePromise.then((data) => (this.performances = data));
    },
    async performancesForThisWeek(): Promise<any> {
      if (this.performancesForWeek) {
        return this.performancesForWeek;
      }

      this.loading = true;

      const plant = this.meterStore.getMeterDetails.plant;
      const startDate = subDays(new Date(), 7);
      const endDate = new Date();

      const result = await new SatelliteApi().getYield(
        plant.owning_company_id,
        plant.id,
        this.meterStore.getMeterDetails.id,
        startDate,
        endDate,
        'actual-yield',
        'hour',
      );

      const performances = result.data.data.map((data) => {
        return {
          start_date: new Date(data.time),
          actual_yield_in_kwh: data.yield_in_wh / 1000,
        };
      })
        .filter((p: object) => this.filterNightlyHours(p.start_date))
        .map((p: object) => ({
          start_date: p.start_date.toISOString(),
          actual_yield_in_kwh: p.actual_yield_in_kwh,
        }));

      this.performancesForWeek = performances;
      this.loading = false;

      return performances;
    },
    filterNightlyHours(date: Date) {
      const monthNumber = getMonth(date);
      const isSummer = monthNumber > 5 && monthNumber < 9;

      return isSummer
        ? getHours(date) < 24 && getHours(date) > 5
        : getHours(date) < 22 && getHours(date) > 7;
    },
    async performancesForYear(): Promise<any> {
      const allPerformances = this.meterStore.getMeterDetails.performances;
      let performancesForYear = allPerformances
        .filter(
          (p) =>
            new Date(p.start_date).getFullYear() ===
            this.selectedPeriod,
        )
        .map((p) => ({...p, month: new Date(p.start_date).getMonth()}));

      if (performancesForYear.length < 12) {
        return this.fillMissingMonthsForYear(performancesForYear);
      }

      return performancesForYear;
    },
    fillMissingMonthsForYear(performancesForYear: Array<object>) {
      // Re-key performances on month
      const performanceByMonth = performancesForYear.reduce(
        (acc, performance) => {
          acc[performance.month] = performance;
          return acc;
        },
        {},
      );

      return [...Array(12).keys()].map((month) => {
        return (
          performanceByMonth[month] ?? {
            start_date: new Date(this.selectedPeriod.toString(), month, 1).toISOString(),
            actual_yield_in_kwh: 0,
          }
        );
      });
    },
    async performancesForThisMonth(): Promise<any> {
      if (this.performancesForMonth) {
        return this.performancesForMonth;
      }

      this.loading = true;

      const plant = this.meterStore.getMeterDetails.plant;
      const startDate = addDays(startOfMonth(new Date()), 1);
      const endDate = new Date();

      const result = await new SatelliteApi().getYield(
        plant.owning_company_id,
        plant.id,
        this.meterStore.getMeterDetails.id,
        startDate,
        endDate,
        'actual-yield',
        'day',
      );

      const performances = result.data.data.map((data) => {
        return {
          start_date: data.time,
          actual_yield_in_kwh: data.yield_in_wh / 1000,
        };
      });

      const daysInMonth = getDaysInMonth(new Date());
      const daysLeftInMonth = daysInMonth - performances.length;

      // Fill in days in the future for this month
      for (let d = 0; d < daysLeftInMonth; d++) {
        const date = startOfDay(addDays(new Date(), d));
        performances.push({ start_date: date.toISOString(), actual_yield_in_kwh: 0 });
      }

      this.performancesForMonth = performances;
      this.loading = false;

      return performances;
    },
  },
  watch: {
    selectedPeriod() {
      this.handlePeriodSelected();
    },
  },
};
</script>

<template>
  <div id="details" class="flex flex-col mt-6 px-4 gap-2">
    <span> Selecteer een periode </span>
    <Select v-model="selectedPeriod" :options="availablePeriods" optionLabel="label"
            optionValue="value"
            :disabled="loading"
            class="w-full justify-center"
    />

    <div class="bg-white rounded-xl">
      <Skeleton v-if="loading" width="100%" height="300px"/>
      <div v-else>
        <performance-chart-hourly v-if="selectedPeriod === currentWeekNumber" :chart-data="performances"/>
        <div v-else-if="selectedPeriod === currentMonthNumber" class="flex flex-col">
          <span class="m-auto"> {{ currentMonthString }} </span>
          <performance-chart
            :chartData="performances"
            date-format-tooltip-title="PP"
            date-format-x-axis="d"
          />
        </div>
        <performance-chart
          v-else
          :chartData="performances"
          date-format-tooltip-title="MMMM"
          date-format-x-axis="MMMMM"
        />
        <span class="flex justify-center text-theme-primary pt-2">In kWh</span>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>